// Las mismas reglas que valida la API, para avisar mientras se escribe y no
// recién al enviar. Los mensajes son los del prototipo aprobado.

import { EDAD_MAXIMA, EDAD_MINIMA, edadEn } from '@medchoice/shared';
import { hoyIso } from '../ui/format';

export type Regla<T> = (valor: string, todos: T) => string | null;

export const LARGOS = {
  nombre: 60, email: 80, clave: 64, telefono: 15, dni: 8, matricula: 20,
  texto: 500, bio: 600, motivo: 300, direccion: 80,
} as const;

const vacio = (v: string) => !v.trim();

export const soloDigitos = (valor: string, maximo: number) => valor.replace(/\D/g, '').slice(0, maximo);

export const requerido = (campo: string) => (valor: string) => (vacio(valor) ? `${campo} es obligatorio.` : null);

export function validarNombreCompleto(valor: string): string | null {
  const limpio = valor.trim();
  if (!limpio) return 'Ingresá tu nombre y apellido.';
  if (limpio.length < 3) return 'El nombre es demasiado corto.';
  if (!/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' .-]+$/.test(limpio)) return 'El nombre solo puede tener letras.';
  if (!/\s/.test(limpio)) return 'Ingresá nombre y apellido.';
  return null;
}

export function validarEmail(valor: string): string | null {
  const limpio = valor.trim();
  if (!limpio) return 'Ingresá tu correo electrónico.';
  if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(limpio)) return 'El correo no tiene un formato válido.';
  return null;
}

export function validarClave(valor: string): string | null {
  if (!valor) return 'Ingresá una contraseña.';
  if (valor.length < 8) return 'La contraseña necesita al menos 8 caracteres.';
  if (!/[0-9]/.test(valor)) return 'La contraseña tiene que incluir al menos un número.';
  if (!/[A-Za-z]/.test(valor)) return 'La contraseña tiene que incluir al menos una letra.';
  return null;
}

/** De 0 a 4, para el medidor de fuerza. */
export function fuerzaDeClave(valor: string): number {
  const puntos = [valor.length >= 8, valor.length >= 12, /[A-Z]/.test(valor), /[0-9]/.test(valor), /[^A-Za-z0-9]/.test(valor)]
    .filter(Boolean).length;
  return Math.min(puntos, 4);
}

export function validarDni(valor: string): string | null {
  if (!valor) return 'Ingresá tu DNI.';
  if (valor.length < 7) return 'El DNI tiene que tener al menos 7 dígitos.';
  if (Number(valor) === 0) return 'El DNI no es válido.';
  return null;
}

export function validarTelefono(valor: string): string | null {
  if (!valor) return 'Ingresá un teléfono de contacto.';
  if (valor.length < 8) return 'El teléfono es demasiado corto.';
  return null;
}

export function validarMatricula(valor: string): string | null {
  const limpio = valor.trim().toUpperCase();
  if (!limpio) return 'Ingresá tu número de matrícula.';
  if (!/^(MN|MP)\s?[\d.]{4,10}$/.test(limpio)) return 'Formato esperado: MN 145.234 o MP 12345.';
  return null;
}

export function validarNacimiento(valor: string): string | null {
  if (!valor) return 'Elegí el día, el mes y el año en que naciste.';
  if (Number.isNaN(new Date(`${valor}T12:00:00`).getTime())) return 'La fecha no es válida.';
  const edad = edadEn(valor, hoyIso());
  if (edad < EDAD_MINIMA) return `Para usar MedChoice tenés que tener al menos ${EDAD_MINIMA} años.`;
  if (edad > EDAD_MAXIMA) return 'Revisá la fecha de nacimiento.';
  return null;
}

/** Separa "Lucía Fernández López" en nombre y apellido, como pide la API. */
export function separarNombre(completo: string): { nombre: string; apellido: string } {
  const [nombre = '', ...resto] = completo.trim().split(/\s+/);
  return { nombre, apellido: resto.join(' ') };
}
